import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';

interface Domain {
  domain: string;
  consent_status: string;
  content_signals: string | null;
  robots_txt_hash: string | null;
  last_checked: string | null;
  next_crawl: string | null;
  recheck_interval_days: number;
  priority: string;
  content_count: number;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

function Domains() {
  const [domains, setDomains] = useState<Domain[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [newDomain, setNewDomain] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    fetchDomains();
  }, [pagination.page, statusFilter]);

  const fetchDomains = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
      });

      if (search) params.set('q', search);
      if (statusFilter) params.set('status', statusFilter);

      const response = await fetch(`${API_BASE_URL}/admin/domains?${params}`);
      if (!response.ok) throw new Error('Failed to fetch domains');
      
      const data = await response.json();
      setDomains(data.domains || []);
      setPagination(data.pagination);
    } catch (err) {
      setError('Failed to load domains');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchDomains();
  };

  const handleAddDomain = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDomain.trim()) return;

    setAdding(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/domains/${newDomain.trim()}/crawl`, {
        method: 'POST',
      });

      if (!response.ok) throw new Error('Failed to add domain');

      setNewDomain('');
      fetchDomains();
    } catch (err) {
      alert('Failed to add domain');
      console.error(err);
    } finally {
      setAdding(false);
    }
  };

  const triggerCrawl = async (domain: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/domains/${domain}/crawl`, {
        method: 'POST',
      });

      if (!response.ok) throw new Error('Failed to trigger crawl');

      alert(`Crawl queued for ${domain}`);
      fetchDomains();
    } catch (err) {
      alert('Failed to trigger crawl');
      console.error(err);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1>Domain Management</h1>
        <p>Manage domains and their crawl settings</p>
      </div>

      {/* Add Domain */}
      <div className="card mb-8">
        <h3 className="mb-4">Add New Domain</h3>
        <form onSubmit={handleAddDomain} className="filter-bar">
          <input
            type="text"
            className="input"
            placeholder="example.com"
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            style={{ maxWidth: '300px' }}
          />
          <button type="submit" className="btn btn-primary" disabled={adding}>
            {adding ? 'Adding...' : 'Add & Crawl'}
          </button>
        </form>
      </div>

      {/* Filters */}
      <form onSubmit={handleSearch} className="filter-bar">
        <input
          type="text"
          className="input"
          placeholder="Search domains..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPagination((prev) => ({ ...prev, page: 1 }));
          }}
        >
          <option value="">All statuses</option>
          <option value="allowed">Allowed</option>
          <option value="denied">Denied</option>
          <option value="unknown">Unknown</option>
        </select>
        <button type="submit" className="btn btn-secondary">
          Search
        </button>
      </form>

      {/* Results */}
      {loading ? (
        <div className="loading-state">Loading domains...</div>
      ) : error ? (
        <div className="empty-state">
          <p>{error}</p>
          <button className="btn btn-primary mt-4" onClick={fetchDomains}>
            Retry
          </button>
        </div>
      ) : domains.length === 0 ? (
        <div className="card empty-state">
          <p>No domains found</p>
          <p className="text-secondary mt-2">
            Add a domain above to start crawling.
          </p>
        </div>
      ) : (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <th>Domain</th>
                <th>Status</th>
                <th>Content</th>
                <th>Priority</th>
                <th>Last Checked</th>
                <th>Next Crawl</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {domains.map((domain) => (
                <tr key={domain.domain}>
                  <td>
                    <Link to={`/admin/domains/${domain.domain}`}>
                      {domain.domain}
                    </Link>
                  </td>
                  <td>
                    <span className={`badge badge-${domain.consent_status === 'allowed' ? 'success' : domain.consent_status === 'denied' ? 'error' : 'warning'}`}>
                      {domain.consent_status}
                    </span>
                  </td>
                  <td>{domain.content_count.toLocaleString()} pages</td>
                  <td>{domain.priority}</td>
                  <td>
                    {domain.last_checked
                      ? new Date(domain.last_checked).toLocaleDateString()
                      : 'Never'}
                  </td>
                  <td>
                    {domain.next_crawl
                      ? new Date(domain.next_crawl).toLocaleDateString()
                      : 'Pending'}
                  </td>
                  <td>
                    <div className="actions">
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => triggerCrawl(domain.domain)}
                      >
                        Crawl Now
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="pagination">
            <button
              className="pagination-btn"
              disabled={pagination.page <= 1}
              onClick={() => handlePageChange(pagination.page - 1)}
            >
              Previous
            </button>
            <span className="pagination-info">
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} domains)
            </span>
            <button
              className="pagination-btn"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => handlePageChange(pagination.page + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default Domains;
